import React from 'react';
import { Flex } from 'components';
import { H4, P } from 'components/typography';
import { Icon } from 'design-system-web';
import { useTranslation } from 'react-i18next';
import { UserThumbnailWithLoader } from 'components/lane/UserThumbnailWithLoader';
import { ServiceRequestFeedback } from 'pages/portal/admin/channel/service-requests/details/components';
import { ServiceRequest } from 'graphql-query-contracts';
import styles from '../Details.scss';
import {
  useGenerateServiceRequestFromPmTaskEnabled,
  useScheduleServiceRequestDueDateEnabled,
} from 'react-hooks';
import { generatePath, Link } from 'react-router-dom';
import { routes } from 'lane-shared/config';
import cx from 'classnames';
import { dateFormatter } from 'lane-shared/helpers/formatters';
import { getChannelTimezone, getBrowserTimeZone } from 'domains/workOrder/serviceRequest/helpers/getChannelTimezone';
import { AddressType } from 'application-types';
import { LONG_DATE_TIME } from 'lane-shared/helpers/constants/dates';

function BillableSection({
  serviceRequestData,
}: {
  serviceRequestData: ServiceRequest;
}) {
  const { t } = useTranslation();

  if (!serviceRequestData.isBillable) return null;

  return (
    <Flex direction="column" width="full">
      <Flex
        direction="column"
        width="full"
        className={styles.ServiceRequestDataRows}
      >
        <Flex
          className={cx(
            styles.ServiceRequestDataRow,
            styles.ServiceRequestBillable
          )}
          width="full"
        >
          <span>{t('web.admin.serviceRequest.billable')}</span>
          <span className={styles.BillableInput}>
            <Icon className={styles.Icon} name="check" />
            {t('web.admin.serviceRequest.billable.yes')}
          </span>
        </Flex>
      </Flex>
    </Flex>
  );
}

interface ServiceRequestDataDisplayProps {
  serviceRequestData: ServiceRequest;
  isPropertyChannel: boolean;
  channel: { slug: string; address?: AddressType };
}

const EMPTY_VALUE = '-';

export function ServiceRequestDataDisplay({
  serviceRequestData,
  channel,
  isPropertyChannel,
}: ServiceRequestDataDisplayProps) {
  const { t } = useTranslation();
  const isPreventiveMaintenanceTaskEnabled =
    useGenerateServiceRequestFromPmTaskEnabled();
  const isScheduleServiceRequestDueDateEnabled =
    useScheduleServiceRequestDueDateEnabled();

  const timeZone = (channel.address && getChannelTimezone(channel.address)) ?? getBrowserTimeZone();

  return (
    <div className={styles.serviceRequestData}>
      <H4 mb={4}>{t`web.admin.serviceRequest.details`}</H4>
      <BillableSection serviceRequestData={serviceRequestData} />
      <Flex direction="column">
        {isPreventiveMaintenanceTaskEnabled &&
          serviceRequestData?.task &&
          serviceRequestData.task.id && (
            <Flex className={styles.ServiceRequestDataRows}>
              <Flex className={styles.ServiceRequestDataRow}>
                <span>
                  {t('web.admin.serviceRequest.preventiveMaintenanceTask')}
                </span>
                <Link
                  key={serviceRequestData.task.id}
                  to={generatePath(routes.channelAdminWorkOrdersPMTaskDetails, {
                    id: channel.slug,
                    taskId: serviceRequestData.task.id,
                  })}
                >
                  {serviceRequestData.task.userFriendlyID}
                </Link>
              </Flex>
            </Flex>
          )}
        <Flex className={styles.ServiceRequestDataRows}>
          {isPropertyChannel && (
            <Flex className={styles.ServiceRequestDataRow}>
              <span>{t('web.admin.serviceRequest.company')} </span>
              <span>{serviceRequestData.company?.name}</span>
            </Flex>
          )}
          {serviceRequestData.feedback && (
            <Flex className={styles.ServiceRequestDataRow}>
              <ServiceRequestFeedback feedback={serviceRequestData.feedback} />
            </Flex>
          )}
          <Flex className={styles.ServiceRequestDataRow}>
            <span>{t('web.admin.serviceRequest.submittedBy')}</span>
            <div className={styles.users}>
              <UserThumbnailWithLoader
                className={styles.user}
                id={serviceRequestData?.createdBy?.id || undefined}
                labelPosition="right"
                splitName={false}
              />
            </div>
          </Flex>
        </Flex>
        <Flex className={styles.ServiceRequestDataRows}>
          <Flex className={styles.ServiceRequestDataRow}>
            <span>{t('web.admin.serviceRequest.category')}</span>
            <span>{serviceRequestData.category}</span>
          </Flex>
          <Flex className={styles.ServiceRequestDataRow}>
            <span>{t('web.admin.serviceRequest.issue')}</span>
            <span>{serviceRequestData.issue}</span>
          </Flex>
          {isPropertyChannel && (
            <>
              <div className={styles.ServiceRequestDataRow} />
              <Flex className={styles.ServiceRequestDataRow}>
                <span>{t('web.admin.serviceRequest.floor')}</span>
                <span>{serviceRequestData.floor}</span>
              </Flex>
              {serviceRequestData.suite && (
                <Flex className={styles.ServiceRequestDataRow}>
                  <span>{t('web.admin.serviceRequest.suite')}</span>
                  <span>{serviceRequestData.suite}</span>
                </Flex>
              )}
            </>
          )}
          <Flex className={styles.ServiceRequestDataRow}>
            <span>{t('web.admin.serviceRequest.location')}</span>
            <span>{serviceRequestData.location}</span>
          </Flex>
        </Flex>
      </Flex>
      <Flex direction="column" width="full">
        <Flex
          direction="column"
          width="full"
          className={styles.ServiceRequestDataRows}
        >
          <Flex className={styles.ServiceRequestDataRow} width="full">
            <span>{t('web.admin.serviceRequest.description')}</span>
            <P>{serviceRequestData.description}</P>
          </Flex>
          {isScheduleServiceRequestDueDateEnabled && (
            <Flex className={styles.ServiceRequestDataRow} width="full">
              <span>{t('web.admin.serviceRequest.scheduledDueDate')}</span>
              {serviceRequestData.scheduledDueDate ? <P>
                {dateFormatter(
                  serviceRequestData.scheduledDueDate,
                  LONG_DATE_TIME,
                  timeZone
                )}
              </P> : <P>{EMPTY_VALUE}</P>}
            </Flex>
          )}
          {serviceRequestData.customQuestions &&
            serviceRequestData.customQuestions.map(
              (customQuestion: any, index: number) => (
                <Flex
                  className={styles.ServiceRequestDataRow}
                  width="full"
                  key={index}
                >
                  <span>{customQuestion.Question}</span>
                  <P>{customQuestion.Answer}</P>
                </Flex>
              )
            )}
        </Flex>
      </Flex>
    </div>
  );
}
